import type { MedicationDose } from '../lib/to-medication-order';
import type { AmbientMedication } from './ambient-types';
import { parseMedicationSig } from './parse-medication-sig';
import { dictatedSig } from './dictated-sig';

// Re-exported: the sentence moved to its own module so the card mapper can read it without pulling
// this file's dose-form imports into the render path (`proposal-render-determinism.test.tsx`).
export { dictatedSig };

/**
 * The dictated prescription, as dose fields the medication dialog can open with.
 *
 * WHY THIS EXISTS. Samer dictated "paracetamol 500 mg three times a day" on the live dev app,
 * ticked it, and the dose dialog opened EMPTY. `AmbientMedication` carries `dose`, `route`,
 * `frequency`, `duration` AND `instructions` off the wire, and both medication accept paths threw
 * all five away — `decideAccept` kept only `source.name` for the search branch, and `toOrderSeed`
 * returned `{ name }` for the dialog branch. So the clinician says the dose out loud and then
 * retypes it from memory.
 *
 * THE PARSING IS NOT NEW. `parse-medication-sig.ts` already does it, correctly, with 148 lines of
 * tests — and had **zero consumers** in the whole repo. Same shape as `ClinicalDisplayName.Synonyms`
 * and `HealthcareEntityExtractor.cs`: built, right, never wired. This module is the twenty lines of
 * adapter that were missing, not a second parser. A first attempt at this DID write a second
 * parser, and it emitted `frequency: 'every_day'` — the exact spelling PROD-1445 records as
 * mis-parsing in the e-prescription path, where the real wire value is `everyday`. Reusing the
 * existing one is not tidiness; it is the difference between correct and broken.
 *
 * WHAT IS DELIBERATELY LEFT ALONE:
 *
 *   - **`route` and `doseUnit` are never set.** Both are CODED and must come from the medication the
 *     clinician actually picked. The dialog already falls through per-field
 *     (`initialDose?.route ?? medication.route?.[0]?.code`), so omitting them means the picked
 *     record's own values are used — which is right. A dictated "PO" the catalogue record does not
 *     offer would otherwise become a dropdown value that is not in the dropdown.
 *   - **Nothing is defaulted.** `parseMedicationSig` returns undefined rather than guessing, and
 *     that is preserved: a frequency nobody stated must not appear on a prescription. PROD-2018
 *     measured a 7x frequency error in QA; the fix for that is fewer inventions, not more.
 *
 * EVERYTHING DICTATED SURVIVES REGARDLESS. Whatever fails to parse is carried into `sig` verbatim,
 * so a clinician can always READ what was said even when no field could be filled. A blank form
 * after someone said the dose aloud is the defect being fixed; an unparsed sentence they can see is
 * an acceptable outcome.
 *
 * This opens a form. It never writes an order — the clinician still confirms and saves.
 */

/**
 * Strength as dictated: "500 mg" -> `{ amount: 500, unit: 'mg' }`.
 *
 * EXPORTED AND NOT USED BY `toDictatedDose`, on purpose. Reading the strength is easy; pairing it
 * with the right unit is not, and the unit belongs to the catalogue row the clinician picks. This
 * exists so a future change that DOES have the picked record can validate or convert against it —
 * and so the tests can demonstrate that the number is readable and deliberately declined.
 */
export function parseStrength(dose: string | undefined): { amount: number; unit: string } | undefined {
  if (!dose) return undefined;
  // Requires a unit to be PRESENT even though it is not used, because a bare number is not a
  // strength — "take 2" is two of what? A quantity with no unit looks filled in and cannot be
  // dispensed against.
  const m =
    /(\d+(?:\.\d+)?)\s*(mcg|microgram|mg|milligram|g|gram|ml|millilitre|milliliter|iu|units?|tabs?|tablets?|caps?|capsules?|puffs?|drops?)\b/i.exec(
      dose,
    );
  if (!m) return undefined;
  const amount = Number(m[1]);
  if (!Number.isFinite(amount) || amount <= 0) return undefined;
  return { amount, unit: m[2]!.toLowerCase() };
}

/**
 * Dose fields for a dictated medication, or `undefined` when nothing was dictated beyond the name.
 *
 * A bare drug name is the ordinary case and returns nothing — there is no point handing the dialog
 * an object with no values in it.
 */
export function toDictatedDose(med: AmbientMedication): MedicationDose | undefined {
  const sig = dictatedSig(med);
  if (!sig) return undefined;

  const parsed = parseMedicationSig({
    // Both are passed even though either may be empty: `parseMedicationSig` parses the two halves
    // independently, so a recognisable frequency still pre-fills when the duration is open-ended.
    ...(med.frequency ? { frequency: med.frequency } : {}),
    ...(med.duration ? { duration: med.duration } : {}),
  });

  const dose: MedicationDose = { sig };

  // `frequency` and its count travel together. `parseFrequency` only returns a frequency it
  // recognised, and only attaches `timesPerDay` when the phrasing stated one.
  if (parsed.frequency) dose.frequency = parsed.frequency;
  if (parsed.timesPerDay !== undefined) dose.timesADay = parsed.timesPerDay;
  if (parsed.dayInterval !== undefined) dose.daysInterval = parsed.dayInterval;
  if (parsed.durationValue !== undefined) dose.duration = parsed.durationValue;
  if (parsed.durationUnit !== undefined) dose.durationUnit = parsed.durationUnit;

  /**
   * `quantity` IS DELIBERATELY NOT SET. This is a correction, and the important one here.
   *
   * The first version read "500 mg" and wrote `quantity: 500`, discarding the unit because the
   * dialog takes its unit from the medication the clinician picked. Cross-model review (Codex,
   * gpt-5.6-sol) showed what that produces when the two disagree:
   *
   *   dictated "500 mg"  +  catalogue row measured in TABLET  ->  500 TABLETS
   *   dictated "0.5 g"   +  catalogue row measured in mg      ->  0.5 mg
   *
   * Both are saveable, and both feed the dispense calculation. A number paired with the wrong unit
   * is worse than an empty field by every measure that matters: it is a prescribing error that
   * looks like a completed form.
   *
   * Converting the unit is not the fix either — it needs the picked record's unit vocabulary, which
   * a pure function cannot see, and a silent unit conversion on a prescription is its own class of
   * defect. So the strength stays in the verbatim `sig`, where the clinician reads it and types it.
   * `parseStrengthAmount` is kept, and tested, precisely to show we can read the number and still
   * decline to use it.
   *
   * `frequency`, `timesADay` and `duration` ARE set, because each is self-describing: a count per
   * day means the same thing against every medication, and `durationUnit` travels with its number.
   */

  return dose;
}
